import { TranslationsType } from '@Localization';
import { ReactNode } from 'react';

const INFINITY = 9999999;
const INFINITY_FLOAT = 9999999.0;

enum PARAMETER_TYPE {
  BOOL = 'bool',
  FLOAT = 'float',
  DOUBLE = 'double',
  INT = 'int',
  ENUM = 'enum',
}

export enum PARAMETER_TYPE_INTERNAL {
  BOOL = 'bool',
  INT = 'int',
  FLOAT = 'float',
  ENUM = 'enum',
}

type LabelType = keyof TranslationsType['params'];

interface IParameterBase {
  name: string;
  label: LabelType;
  type: PARAMETER_TYPE_INTERNAL;
  itemDescription?: IParameterDto['config']['itemDescription'];
}

export interface IParameterNumeric extends IParameterBase {
  config: {
    max: number;
    min: number;
    step: number;
    validator: (value: string | null) => boolean;
  };
}

interface IParameterFloat extends IParameterNumeric {
  type: PARAMETER_TYPE_INTERNAL.FLOAT;
}

interface IParameterInt extends IParameterNumeric {
  type: PARAMETER_TYPE_INTERNAL.INT;
}

export interface IParameterBoolean extends IParameterBase {
  type: PARAMETER_TYPE_INTERNAL.BOOL;
}

export type Parameter = IParameterFloat | IParameterInt | IParameterBoolean;
export type Parameters = Parameter[];

export interface IParameterDto {
  name: string;
  label: LabelType;
  type: PARAMETER_TYPE;
  config: {
    def?: number | string;
    max?: number | string;
    min?: number | string;
    step?: number | string;
    inTrainContinuation?: boolean;
    itemDescription?: {
      description: string | null;
      category: string | null;
      shortName: string | null;
      displayedName: string | null;
    };
  };
}

function createIntParameter(name: string, label: LabelType, config: IParameterDto['config']): IParameterInt {
  const paramConfig = {
    min: Number(config.min) || 0,
    max: Number(config.max) || INFINITY,
    step: Number(config.step) || 1,
    def: Number(config.def) || null,
  };

  return {
    name,
    label,
    type: PARAMETER_TYPE_INTERNAL.INT,
    itemDescription: config.itemDescription,
    config: {
      ...paramConfig,
      validator(value: string | null): boolean {
        if (paramConfig.def === null && value === null) {
          return true;
        }

        if (value === null) {
          return false;
        }

        const asInt = parseInt(value, 10);

        return !isNaN(asInt) && asInt >= paramConfig.min && asInt <= paramConfig.max;
      },
    },
  };
}

function createFloatParameter(name: string, label: LabelType, config: IParameterDto['config']): IParameterFloat {
  const paramConfig = {
    min: config.min ? parseFloat(config.min.toString()) : 0.0,
    max: config.max ? parseFloat(config.max.toString()) : INFINITY_FLOAT,
    step: config.step ? parseFloat(config.step.toString()) : 0.1,
    def: config.def ? parseFloat(config.def.toString()) : null,
  };

  return {
    name,
    label,
    type: PARAMETER_TYPE_INTERNAL.FLOAT,
    itemDescription: config.itemDescription,
    config: {
      ...paramConfig,
      validator(value: string | null): boolean {
        if (paramConfig.def === null && value === null) {
          return true;
        }

        if (value === null) {
          return false;
        }

        const asFloat = parseFloat(value);

        return !isNaN(asFloat) && asFloat >= paramConfig.min && asFloat <= paramConfig.max;
      },
    },
  };
}

function createBoolParameter(name: string, label: LabelType, config: IParameterDto['config']): IParameterBoolean {
  return {
    name,
    label,
    itemDescription: config.itemDescription,
    type: PARAMETER_TYPE_INTERNAL.BOOL,
  };
}

export function parseParameters(parametersToParse: IParameterDto[]): {
  parsedParameters: Parameters;
  preselectedValues: Record<string, number | null | boolean>;
} {
  const preselectedValues: Record<string, number | null | boolean> = {};

  const parsedParameters = parametersToParse.reduce<Parameters>((acc, { type, name, label, config }) => {
    switch (type) {
      case PARAMETER_TYPE.INT:
        acc.push(createIntParameter(name, label, config));

        preselectedValues[name] = Number(config.def) || null;
        break;
      case PARAMETER_TYPE.FLOAT:
      case PARAMETER_TYPE.DOUBLE:
        acc.push(createFloatParameter(name, label, config));

        preselectedValues[name] = config.def ? parseFloat(config.def.toString()) : null;
        break;
      case PARAMETER_TYPE.BOOL: {
        acc.push(createBoolParameter(name, label, config));
        preselectedValues[name] = false;
        break;
      }
      case PARAMETER_TYPE.ENUM:
        break;
    }

    return acc;
  }, []);

  return {
    parsedParameters,
    preselectedValues,
  };
}

const UNCATEGORIZED_KEY = 'General';
const CATEGORIES_RIGHT_COL = ['Augmentations'];

function groupParameters(parameters: Parameters): Record<string, Parameters> {
  return parameters.reduce<Record<string, Parameters>>((acc, parameter) => {
    const categoryKey = parameter.itemDescription?.category ?? UNCATEGORIZED_KEY;

    if (acc[categoryKey] === undefined) {
      acc[categoryKey] = [];
    }

    acc[categoryKey].push(parameter);

    return acc;
  }, {});
}

function splitGroups(groupedParameters: Record<string, Parameter[]>, renderRightCol?: boolean): Record<string, Parameter[]> {
  return Object.keys(groupedParameters).reduce((acc: Record<string, Parameter[]>, item) => {
    if (renderRightCol && CATEGORIES_RIGHT_COL.includes(item)) {
      acc[item] = groupedParameters[item];
    }
    if (!renderRightCol && !CATEGORIES_RIGHT_COL.includes(item)) {
      acc[item] = groupedParameters[item];
    }

    return acc;
  }, {});
}

export const mapSchemeToForm = (params: Parameters, createFormField: (item: Parameter, key: number) => ReactNode, renderRightCol?: boolean): ReactNode[] => {
  const groupedParameters = groupParameters(params);
  const splittedGroups = splitGroups(groupedParameters, renderRightCol);

  const defaultParameters = splittedGroups[UNCATEGORIZED_KEY] ?? [];
  const formArray = defaultParameters.length
    ? [
        <div key={UNCATEGORIZED_KEY}>
          <h5 className='name'>{UNCATEGORIZED_KEY}</h5>
          {defaultParameters.reduce<ReactNode[]>((acc, item, key) => {
            acc.push(createFormField(item, key));
            return acc;
          }, [])}
        </div>,
      ]
    : [];

  const otherGroups = Object.keys(splittedGroups).filter((key) => key !== UNCATEGORIZED_KEY);

  return otherGroups.reduce<ReactNode[]>((acc, key) => {
    const formFields = splittedGroups[key].reduce<ReactNode[]>((acc, item, key) => {
      acc.push(createFormField(item, key));
      return acc;
    }, []);

    acc.push(
      <div key={key}>
        <h5 className='name'>{key}</h5>
        {formFields}
      </div>
    );
    return acc;
  }, formArray);
};
